import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Waveform as WaveformIcon } from "@phosphor-icons/react";
import { getBytes, ref as storageRef } from "firebase/storage";
import { AppShell } from "@/components/layout/AppShell";
import { Spinner } from "@/components/ui/Spinner";
import { AudioUploader } from "@/components/upload/AudioUploader";
import { TextUploader } from "@/components/upload/TextUploader";
import { ProcessButton } from "@/components/studio/ProcessButton";
import { RecorderPanel } from "@/components/studio/RecorderPanel";
import { StudioTabs } from "@/components/studio/StudioTabs";
import { TakeList } from "@/components/studio/TakeList";
import { CrashRecoveryBanner } from "@/components/studio/CrashRecoveryBanner";
import { createTake } from "@/lib/firestore/takes";
import { getPipelineStageByName } from "@/lib/firestore/pipeline";
import { storage } from "@/config/firebase";
import { useChapters } from "@/hooks/useChapters";
import { useProject } from "@/hooks/useProject";
import { useRecorder } from "@/hooks/useRecorder";
import { useStudioMarkers } from "@/hooks/useStudioMarkers";
import { useTakes } from "@/hooks/useTakes";
import { createStudioMarker } from "@/lib/firestore/studioMarkers";
import { PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";
import type { Chapter, StateMachineState, Take, TranscriptSegment, UnifiedTranscript } from "@/types";

type TranscriptState =
  | { status: "loading" }
  | { status: "empty"; message: string }
  | { status: "error"; message: string }
  | { status: "ready"; transcript: UnifiedTranscript };

export function StudioPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { project, loading: projectLoading } = useProject(projectId ?? null);
  const chaptersState = useChapters(projectId ?? null);
  const takesState = useTakes(projectId ?? null);
  const markersState = useStudioMarkers(projectId ?? null);
  const recorder = useRecorder(projectId ?? "");
  const [studioNotice, setStudioNotice] = useState<string | null>(null);
  const [transcriptState, setTranscriptState] = useState<TranscriptState>({ status: "loading" });

  useEffect(() => {
    if (!projectId) {
      setTranscriptState({ status: "empty", message: "No book run is selected." });
      return;
    }

    let cancelled = false;
    const currentProjectId = projectId;

    async function loadTranscript() {
      setTranscriptState({ status: "loading" });
      try {
        const transcriptionStage = await getPipelineStageByName(currentProjectId, "transcription");
        const artifact = transcriptionStage?.artifact_refs.find((ref) => ref.type === "transcript");
        if (!artifact) {
          if (!cancelled) {
            setTranscriptState({
              status: "empty",
              message: "No transcript exists yet. Bring in source, then run the press.",
            });
          }
          return;
        }

        const bytes = await getBytes(storageRef(storage, artifact.storage_path));
        const decoded = new TextDecoder().decode(bytes);
        const parsed: unknown = JSON.parse(decoded);
        if (!isUnifiedTranscript(parsed)) {
          throw new Error("Transcript artifact is not in the expected UnifiedTranscript format.");
        }

        if (!cancelled) setTranscriptState({ status: "ready", transcript: parsed });
      } catch (error) {
        if (!cancelled) {
          setTranscriptState({
            status: "error",
            message: error instanceof Error ? error.message : "Transcript artifact could not be loaded.",
          });
        }
      }
    }

    void loadTranscript();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleAudioUploadComplete = useCallback(async (storagePaths: string[]) => {
    if (!projectId) return;
    for (let i = 0; i < storagePaths.length; i++) {
      await createTake(projectId, {
        type: "audio_upload",
        title: `Audio source ${takesState.nextOrder + i + 1}`,
        duration_seconds: 0,
        storage_path: storagePaths[i],
        format: storagePaths[i].split(".").pop() ?? "unknown",
        mime_type: "audio/mpeg",
        size_bytes: 0,
        status: "ready",
        order: takesState.nextOrder + i,
      });
    }
  }, [projectId, takesState.nextOrder]);

  const handleTextUploadComplete = useCallback(async (storagePaths: string[]) => {
    if (!projectId) return;
    for (let i = 0; i < storagePaths.length; i++) {
      await createTake(projectId, {
        type: "text_upload",
        title: `Text source ${takesState.nextOrder + i + 1}`,
        duration_seconds: 0,
        storage_path: storagePaths[i],
        format: storagePaths[i].split(".").pop() ?? "txt",
        mime_type: "text/plain",
        size_bytes: 0,
        status: "ready",
        order: takesState.nextOrder + i,
      });
    }
  }, [projectId, takesState.nextOrder]);

  if (projectLoading) {
    return <AppShell variant="auth"><div className="flex justify-center py-20"><Spinner /></div></AppShell>;
  }

  if (!project || !projectId) {
    return (
      <AppShell variant="auth">
        <div className="py-20 text-center font-display text-3xl text-ink-1">Book run not found</div>
      </AppShell>
    );
  }

  const isRecording = recorder.status === "recording" || recorder.status === "paused";
  const transcript = transcriptState.status === "ready" ? transcriptState.transcript : null;
  const sourceCount = takesState.takes.length;
  const chapterCount = chaptersState.chapters.length;

  const addMarker = async (label: string) => {
    try {
      await createStudioMarker(projectId, {
        label,
        time_seconds: recorder.elapsedSeconds,
      });
      setStudioNotice(`${label} added at ${formatClock(recorder.elapsedSeconds)}.`);
    } catch (error) {
      setStudioNotice(error instanceof Error ? error.message : "Studio marker could not be saved.");
    }
  };

  return (
    <AppShell variant="auth">
      <div className="mx-auto max-w-[1360px] px-4 py-5 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <PressEyebrow>Source room</PressEyebrow>
            <h1 className="mt-2 font-display text-[clamp(1.9rem,3vw,3rem)] font-semibold leading-tight text-ink-1">{project.title}</h1>
            <p className="mt-2 max-w-2xl font-ui text-sm leading-as-read text-ink-3">
              Bring in the voice, pages, and fragments PullPress will turn into a manuscript.
            </p>
          </div>
          <Link to={`/project/${projectId}`}>
            <button type="button" className="inline-flex min-h-11 items-center justify-center rounded-as-2 bg-walnut px-5 py-2 font-ui text-sm font-semibold text-paper-raised shadow-as-1">
              Production status
            </button>
          </Link>
        </div>

        {(takesState.error || chaptersState.error || markersState.error) && (
          <div className="mb-4 rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[10px] uppercase tracking-as-eyebrow text-flag">
            {takesState.error ?? chaptersState.error ?? markersState.error}
          </div>
        )}

        <StudioFlowStrip sourceCount={sourceCount} transcriptReady={Boolean(transcript)} chapterCount={chapterCount} projectState={project.state} />

        <div className="grid gap-4 xl:grid-cols-[260px_minmax(0,1fr)_320px]">
          <aside className="space-y-4">
            <PressPanel className="p-4">
              <PressEyebrow>Source index</PressEyebrow>
              <h2 className="mt-4 font-display text-xl font-semibold text-ink-1">What the book is made from</h2>
              <p className="mt-1 font-ui text-sm text-ink-3">{statusLabel(project.state)}</p>
              <div className="mt-4 overflow-hidden rounded-as-2 border border-rule">
                <SourceIndex
                  chapters={chaptersState.chapters}
                  chaptersLoading={chaptersState.loading}
                  takes={takesState.takes}
                  takesLoading={takesState.loading}
                  isRecording={isRecording}
                />
              </div>
            </PressPanel>

            <PressPanel className="p-4">
              <PressEyebrow>Raw material</PressEyebrow>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Metric label="Audio" value={formatDuration(takesState.totalDurationSeconds)} />
                <Metric label="Sources" value={`${takesState.readyCount}/${takesState.totalCount}`} />
                <Metric label="Chapters" value={String(chapterCount)} />
                <Metric label="Transcript" value={transcript ? `${transcript.total_word_count.toLocaleString()} words` : "Not generated"} />
              </div>
            </PressPanel>
          </aside>

          <main className="min-w-0 space-y-4">
            {recorder.hasOrphanedChunks && (
              <CrashRecoveryBanner
                projectId={projectId}
                nextOrder={takesState.nextOrder}
                mimeType={recorder.mimeType}
                fileExtension={recorder.fileExtension}
                onRecovered={() => {}}
                onDiscard={() => {}}
                recoverChunks={recorder.recoverChunks}
                clearRecovery={recorder.clearRecovery}
              />
            )}

            <PressPanel className="p-4">
              <div className="flex flex-col justify-between gap-3 border-b border-rule pb-4 md:flex-row md:items-center">
                <div>
                  <PressEyebrow dot>{isRecording ? "Capturing source" : "Bring in source material"}</PressEyebrow>
                  <h2 className="mt-2 font-display text-3xl font-semibold text-ink-1">{project.title}</h2>
                </div>
                <div className="flex gap-2">
                  <StatusPill tone={recorder.isSupported ? "ready" : "blocked"}>
                    {recorder.isSupported ? recorder.fileExtension.toUpperCase() : "Unsupported"}
                  </StatusPill>
                  <StatusPill tone={sourceCount > 0 ? "ready" : "draft"}>{sourceCount} source{sourceCount === 1 ? "" : "s"}</StatusPill>
                </div>
              </div>
              <div className="mt-5">
                <StudioTabs
                  recordingDisabled={!recorder.isSupported}
                  recordPanel={<RecorderPanel projectId={projectId} nextOrder={takesState.nextOrder} onTakeCreated={() => {}} />}
                  audioPanel={<AudioUploader projectId={projectId} onUploadComplete={handleAudioUploadComplete} />}
                  textPanel={<TextUploader projectId={projectId} onUploadComplete={handleTextUploadComplete} />}
                />
              </div>
            </PressPanel>

            <PressPanel className="p-4">
              <div className="flex flex-col justify-between gap-3 border-b border-rule pb-3 sm:flex-row sm:items-center">
                <div>
                  <PressEyebrow>Memory markers</PressEyebrow>
                  <p className="mt-1 font-ui text-sm text-ink-3">Mark the moments the manuscript should not miss.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { void addMarker("Story beat"); }} className="min-h-11 rounded-as-2 border border-rule bg-paper px-3 py-2 font-ui text-sm text-ink-2">Mark a story beat</button>
                  <button type="button" onClick={() => { void addMarker("Author note"); }} className="min-h-11 rounded-as-2 border border-rule bg-paper px-3 py-2 font-ui text-sm text-ink-2">Add author note</button>
                </div>
              </div>
              {markersState.loading ? (
                <div className="mt-3"><Spinner /></div>
              ) : markersState.markers.length > 0 ? (
                <div className="mt-3 divide-y divide-rule-faint">
                  {markersState.markers.map((marker, index) => (
                    <div key={marker.id} className="grid grid-cols-[34px_1fr_80px] gap-3 py-2">
                      <span className="font-mono text-[10px] text-ink-4">{index + 1}</span>
                      <span className="font-ui text-sm text-ink-1">{marker.label}</span>
                      <span className="text-right font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{formatClock(marker.time_seconds)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No memory markers yet" body="Start source capture, then mark the moments the manuscript should remember." />
              )}
              {studioNotice && (
                <div className="mt-4 rounded-as-2 border border-verify bg-verify-tint p-3 font-mono text-[10px] uppercase tracking-as-eyebrow text-verify">
                  {studioNotice}
                </div>
              )}
            </PressPanel>

            <PressPanel className="p-4">
              <TakeList takes={takesState.takes} loading={takesState.loading} />
              <ProcessButton
                projectId={projectId}
                takes={takesState.takes}
                readyCount={takesState.readyCount}
                isRecording={isRecording}
                isOverLimit={takesState.isOverLimit}
                pricingTier={project.pricing_tier}
                paidTiers={project.paid_tiers ?? []}
              />
            </PressPanel>
          </main>

          <aside className="min-w-0 space-y-4">
            <PressPanel className="p-0">
              <div className="flex items-center justify-between border-b border-rule px-4 py-3">
                <PressEyebrow className="text-walnut-deep">Transcript</PressEyebrow>
                <StatusPill tone={transcript ? "ready" : transcriptState.status === "error" ? "blocked" : "draft"}>
                  {transcript ? "Generated" : "Not generated"}
                </StatusPill>
              </div>
              <TranscriptPanel state={transcriptState} />
            </PressPanel>

            <PressPanel className="p-4">
              <div className="flex items-center justify-between">
                <PressEyebrow>Chapter map</PressEyebrow>
                <StatusPill tone={chapterCount > 0 ? "ready" : "draft"}>{chapterCount}</StatusPill>
              </div>
              <ChapterStructurePanel chapters={chaptersState.chapters} loading={chaptersState.loading} />
            </PressPanel>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function StudioFlowStrip({
  sourceCount,
  transcriptReady,
  chapterCount,
  projectState,
}: {
  sourceCount: number;
  transcriptReady: boolean;
  chapterCount: number;
  projectState: StateMachineState;
}) {
  const draftReady = projectState === "DRAFTED" || projectState === "AWAITING_REVIEW" || projectState === "REVISED" || projectState === "EDITING" || projectState === "EDITED" || projectState === "PACKAGING" || projectState === "PACKAGED" || projectState === "PUBLISHING" || projectState === "PUBLISHED";
  const items = [
    { label: "Source", value: sourceCount > 0 ? `${sourceCount} captured` : "Bring source", tone: sourceCount > 0 ? "ready" : "active" },
    { label: "Transcript", value: transcriptReady ? "Generated" : "Not yet", tone: transcriptReady ? "ready" : sourceCount > 0 ? "active" : "pending" },
    { label: "Map", value: chapterCount > 0 ? `${chapterCount} chapters` : "Not yet", tone: chapterCount > 0 ? "ready" : transcriptReady ? "active" : "pending" },
    { label: "Manuscript", value: draftReady ? "Ready" : "Not yet", tone: draftReady ? "ready" : chapterCount > 0 ? "active" : "pending" },
  ] as const;

  return (
    <PressPanel className="mb-4 p-3">
      <div className="grid gap-2 sm:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className={`rounded-as-2 border px-3 py-2 ${item.tone === "ready" ? "border-verify-soft/40 bg-verify-tint" : item.tone === "active" ? "border-walnut bg-walnut-tint" : "border-rule bg-paper"}`}>
            <p className={`font-ui text-sm font-semibold ${item.tone === "ready" ? "text-verify" : item.tone === "active" ? "text-walnut-deep" : "text-ink-3"}`}>{item.label}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{item.value}</p>
          </div>
        ))}
      </div>
    </PressPanel>
  );
}

function SourceIndex({
  chapters,
  chaptersLoading,
  takes,
  takesLoading,
  isRecording,
}: {
  chapters: Chapter[];
  chaptersLoading: boolean;
  takes: Take[];
  takesLoading: boolean;
  isRecording: boolean;
}) {
  if (chaptersLoading || takesLoading) {
    return <div className="p-4"><Spinner /></div>;
  }

  if (chapters.length > 0) {
    return (
      <div className="divide-y divide-rule-faint">
        {chapters.map((chapter) => (
          <div key={chapter.id} className="bg-paper-raised p-3">
            <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">Chapter {chapter.order}</p>
            <p className="mt-1 font-ui text-sm font-semibold leading-tight text-ink-1">{chapter.title}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
              {chapter.word_count > 0 ? `${chapter.word_count.toLocaleString()} words` : chapter.status}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (takes.length > 0) {
    return (
      <div className="divide-y divide-rule-faint">
        {takes.map((take) => (
          <div key={take.id} className="bg-paper-raised p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">Source {take.order + 1}</p>
                <p className="mt-1 font-ui text-sm font-semibold leading-tight text-ink-1">{take.title}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">{takeMeta(take)}</p>
              </div>
              {take.type !== "text_upload" && <WaveformIcon size={18} className="text-walnut" />}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-paper-raised p-4">
      <EmptyState
        title={isRecording ? "Capturing source" : "No source material yet"}
        body={isRecording ? "The first source will appear here after capture is saved." : "Speak, add audio, or add pages to give the book its first raw material."}
      />
    </div>
  );
}

function TranscriptPanel({ state }: { state: TranscriptState }) {
  if (state.status === "loading") {
    return <div className="p-4"><Spinner /></div>;
  }

  if (state.status === "empty" || state.status === "error") {
    return (
      <div className="p-4">
        <EmptyState title={state.status === "error" ? "Transcript unavailable" : "No transcript yet"} body={state.message} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="space-y-4">
        {state.transcript.segments.map((segment) => (
          <TranscriptSegmentRow key={segment.segment_id} segment={segment} />
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between border-t border-rule pt-4">
        <PressEyebrow>{formatDuration(state.transcript.total_duration_seconds)}</PressEyebrow>
        <PressEyebrow>{state.transcript.total_word_count.toLocaleString()} words</PressEyebrow>
      </div>
    </div>
  );
}

function TranscriptSegmentRow({ segment }: { segment: TranscriptSegment }) {
  return (
    <div className="grid grid-cols-[56px_1fr] gap-3">
      <span className="font-mono text-[10px] text-ink-4">{formatClock(segment.start_seconds)}</span>
      <div>
        <p className="font-ui text-sm leading-as-read text-ink-1">{segment.text}</p>
        {segment.low_confidence && (
          <p className="mt-1 font-mono text-[9px] uppercase tracking-as-eyebrow text-walnut-deep">Low confidence</p>
        )}
      </div>
    </div>
  );
}

function ChapterStructurePanel({ chapters, loading }: { chapters: Chapter[]; loading: boolean }) {
  if (loading) return <div className="mt-4"><Spinner /></div>;

  if (chapters.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState
          title="No chapter map yet"
          body="Run the press after source is ready to create the first chapter structure."
        />
      </div>
    );
  }

  return (
    <div className="mt-4 divide-y divide-rule-faint">
      {chapters.map((chapter) => (
        <div key={chapter.id} className="py-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-ui text-sm font-semibold text-ink-1">{chapter.order}. {chapter.title}</p>
              <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">{chapter.summary}</p>
            </div>
            <span className="shrink-0 font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{chapter.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-as-2 border border-dashed border-rule bg-paper p-4">
      <p className="font-ui text-sm font-semibold text-ink-1">{title}</p>
      <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">{body}</p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
      <p className="mt-1 font-ui text-sm font-semibold leading-as-ui text-ink-1">{value}</p>
    </div>
  );
}

function takeMeta(take: Take): string {
  if (take.type === "text_upload") return `${take.format.toUpperCase()} pages`;
  if (take.duration_seconds > 0) return formatDuration(take.duration_seconds);
  return `${take.format.toUpperCase()} audio`;
}

function statusLabel(state: StateMachineState): string {
  const labels: Record<StateMachineState, string> = {
    CREATED: "Raw source is waiting.",
    INGESTED: "Source is in the press.",
    TRANSCRIBING: "Voice is becoming pages.",
    TRANSCRIBED: "Transcript is ready.",
    ANALYZING: "PullPress is finding the book shape.",
    ANALYZED: "Book shape is ready.",
    STRUCTURING: "Chapter map is being built.",
    STRUCTURED: "Chapter map is ready.",
    DRAFTING: "Manuscript is being built.",
    DRAFTED: "Proof is ready.",
    AWAITING_REVIEW: "Proof window is open.",
    REVISED: "Revision is in.",
    AUTO_ADVANCED: "Proof window closed.",
    EDITING: "Editor is at work.",
    EDITED: "Edit is complete.",
    PACKAGING: "Release packet is being built.",
    PACKAGED: "Release packet is ready.",
    PUBLISHING: "Release is underway.",
    PUBLISHED: "Book is published.",
    ERROR: "Book run is blocked.",
  };
  return labels[state];
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = Math.floor(seconds % 60);
  if (hours > 0) return `${hours}:${minutes.toString().padStart(2, "0")}:${remainder.toString().padStart(2, "0")}`;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

function formatClock(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainder = safeSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainder.toString().padStart(2, "0")}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isTranscriptSegment(value: unknown): value is TranscriptSegment {
  if (!isRecord(value)) return false;
  return (
    isString(value.segment_id) &&
    isString(value.speaker_id) &&
    isNumber(value.start_seconds) &&
    isNumber(value.end_seconds) &&
    isString(value.text) &&
    isNumber(value.confidence) &&
    isBoolean(value.low_confidence)
  );
}

function isUnifiedTranscript(value: unknown): value is UnifiedTranscript {
  if (!isRecord(value)) return false;
  return (
    isString(value.project_id) &&
    isNumber(value.total_duration_seconds) &&
    isNumber(value.total_word_count) &&
    Array.isArray(value.speakers) &&
    Array.isArray(value.segments) &&
    value.segments.every(isTranscriptSegment)
  );
}
